"use client";

import { useEffect, useState } from "react";
import { LayoutGrid } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ChartCard, type SectionModule } from "@/components/chart-card";
import { createClient } from "@/lib/supabase/client";
import { cn } from "@/lib/utils";

export type ChartLayoutMode = "grid" | "single";

const STORAGE_KEY = "erikonomie:chart-layout-pref";

function readGuestPref(): ChartLayoutMode {
  if (typeof window === "undefined") return "grid";
  return window.localStorage.getItem(STORAGE_KEY) === "single" ? "single" : "grid";
}

/**
 * Kachel-Raster mit Umschaltmöglichkeit auf die bisherige Einzelansicht (backlog.md ->
 * §A "Kachel-Layout") - die einzige von Erik ausdrücklich beauftragte Ausnahme von der
 * "Layout nicht verändern"-Regel (siehe CLAUDE.md). Präferenz persistiert über
 * `profiles.chart_layout_pref` (eingeloggt) bzw. `localStorage` (Gast), gleiches
 * additive Muster wie das übrige Local-Portfolio-System. Rendert nur das Chrome - jedes
 * `module.chart` ist eine unveränderte, bereits bestehende Chart-Komponente.
 */
export function SectionLayout({
  sectionTitle,
  companyName,
  modules,
}: {
  sectionTitle: string;
  companyName: string;
  modules: SectionModule[];
}) {
  const [mode, setMode] = useState<ChartLayoutMode>("grid");
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    const supabase = createClient();
    let cancelled = false;

    supabase.auth.getUser().then(({ data }) => {
      if (cancelled) return;
      const uid = data.user?.id ?? null;
      setUserId(uid);
      if (!uid) {
        setMode(readGuestPref());
        return;
      }
      supabase
        .from("profiles")
        .select("chart_layout_pref")
        .eq("id", uid)
        .maybeSingle()
        .then(({ data: profile }) => {
          if (cancelled) return;
          if (!profile) {
            setMode(readGuestPref());
            return;
          }
          const pref = (profile as { chart_layout_pref: string }).chart_layout_pref;
          setMode(pref === "single" ? "single" : "grid");
        });
    });

    return () => {
      cancelled = true;
    };
  }, []);

  async function toggleMode() {
    const next: ChartLayoutMode = mode === "grid" ? "single" : "grid";
    setMode(next);
    if (userId) {
      const supabase = createClient();
      await supabase.from("profiles").update({ chart_layout_pref: next }).eq("id", userId);
    } else if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, next);
    }
  }

  if (modules.length === 0) return null;

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-4">
        <div />
        <h2 className="justify-self-center text-center font-serif text-lg font-semibold text-ink">
          {sectionTitle} für <span className="font-semibold">{companyName}</span>
        </h2>
        <Button
          type="button"
          variant="default"
          size="sm"
          onClick={toggleMode}
          className="justify-self-end gap-2 rounded-full"
        >
          <LayoutGrid className="size-4" />
          Chartlayout ändern
        </Button>
      </div>

      <div className={cn("grid gap-4", mode === "grid" ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1")}>
        {modules.map((module) => (
          <ChartCard key={module.key} module={module} />
        ))}
      </div>
    </div>
  );
}
